import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Round Recon';
const DEFAULT_DESCRIPTION =
  'Round Recon — find in-stock ammunition by caliber, brand, and use case across many retailers.';
const DEFAULT_OG_IMAGE = '/og-default.png';

/**
 * Per-page SEO metadata wrapper (spec §16.4).
 *
 * Renders the document head pieces every public page needs: a unique
 * `<title>`, a meta description, the canonical URL, Open Graph +
 * Twitter card tags, and any page-specific JSON-LD structured data.
 *
 * - `title` is suffixed with "— Round Recon" automatically unless it
 *   already ends with the brand name (avoids "X — Round Recon — Round
 *   Recon").
 * - `noindex` flips the robots directive — use on dashboards, account
 *   pages, and any view that shouldn't be search-indexed.
 * - `jsonLd` is serialized as one or more `<script type=
 *   "application/ld+json">` blocks; pass either a single object or an
 *   array.
 *
 * Builders in `web/src/utils/seo/jsonLd.ts` produce the structured
 * data objects. Pages should derive JSON-LD from the real API payload,
 * not literal strings.
 */
export interface SeoProps {
  title: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  ogImageAlt?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: object | object[];
}

/**
 * @param props {SeoProps}
 * @returns {JSX.Element}
 */
export function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  noindex = false,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = SITE_NAME,
  twitterCard = 'summary_large_image',
  jsonLd,
}: SeoProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`;
  const trimmedDescription =
    description.length > 160 ? `${description.slice(0, 157)}…` : description;
  const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={trimmedDescription} />
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={trimmedDescription} />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={trimmedDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* JSON-LD structured data (one <script> per item). */}
      {jsonLdItems.map((item, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

export default Seo;
